import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowClockwise, ClipboardText } from 'shared/icons';
import {
    ChannelLogo,
    CleanIdentityValue,
    FormatShortInstant,
    SectorBlockNumbers,
} from '../../components/pedidos/pedidosShared';
import { InspectionStation } from '../../components/rma/inspectionStation';
import {
    ChannelMeta,
    PEDIDO_TAG_META,
    SECTOR_META,
    SECTOR_ORDER,
    SYNC_ALL_JOB_LABELS,
    SYNC_ALL_POLL_MS,
    SYNC_JOB_FAILED,
    SYNC_JOB_RUNNING,
    SYNC_ROUND_STATE_LABELS,
    SyncAllJobLabel,
    TagLabel,
} from '../../constants/pedidoTags';
import api from '../../services/api';
import { FormatInt } from '../../utils/format';

// ── Peças locais da lista da Gestão de Pedidos (`/pedidos`) ──────────────────
// Só o que a lista usa e nenhuma outra tela precisa. O que é compartilhado com
// o detalhe mora em components/pedidos/pedidosShared.jsx. Linguagem visual
// plana do módulo RMA: cantos quadrados, negrito só na identidade e na ação
// primária, cor com significado.

/** Campo plano (busca, datas, selects) — mesmo traço do input de busca de Rmas.jsx. */
export const FLAT_FIELD_CLASSES =
    'border border-mono-300 bg-white px-2.5 py-1.5 text-sm text-black outline-none transition-colors focus:border-black disabled:opacity-50 dark:border-mono-700 dark:bg-mono-950 dark:text-white dark:focus:border-white';

/** Ação PRIMÁRIA (única em negrito além da identidade) e ação secundária. */
export const PRIMARY_BUTTON_CLASSES =
    'inline-flex items-center gap-1.5 bg-black px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-mono-800 disabled:opacity-50 dark:bg-white dark:text-black dark:hover:bg-mono-100';
export const SECONDARY_BUTTON_CLASSES =
    'inline-flex items-center gap-1.5 border border-mono-300 bg-white px-3 py-1.5 text-sm text-mono-600 transition-colors hover:border-black hover:text-black disabled:opacity-50 disabled:hover:border-mono-300 disabled:hover:text-mono-600 dark:border-mono-700 dark:bg-mono-950 dark:text-mono-300 dark:hover:border-white dark:hover:text-white';

// ── Identidade multi-canal (decisões 2 e 6 da rodada 2; logo na rodada 3) ───

/**
 * Coluna "Canal": SÓ a logo do marketplace (célula estreita). `ChannelLogo`
 * resolve o asset por `CHANNEL_META` (constants/pedidoTags) e cai em texto
 * curto quando o canal não tem asset; sem canal, "—".
 */
export const ChannelCell = ({ channel }) => (
    <span className="flex items-center">
        <ChannelLogo channel={channel} size="md" />
    </span>
);

/**
 * Selo textual curto do canal (ML/Shopee/…) — alternativa compacta à logo,
 * mantida para quem precisar de texto; a lista usa `ChannelCell`. Sem canal
 * no wire não renderiza nada.
 */
export const ChannelBadge = ({ channel }) => {
    const meta = ChannelMeta(CleanIdentityValue(channel));
    if (!meta) {
        return null;
    }
    return (
        <span
            title={meta.label}
            className="inline-flex shrink-0 items-center bg-mono-100 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-mono-500 dark:bg-mono-900 dark:text-mono-400"
        >
            {meta.short}
        </span>
    );
};

/**
 * Coluna "Pedido do canal": nº do pedido no canal em negrito mono (a
 * manchete da linha). O canal tem coluna própria; `channel` só acrescenta o
 * selo textual quando alguém ainda quiser a forma antiga.
 */
export const OrderIdentityCell = ({ orderId, channel = null }) => (
    <span className="flex min-w-0 items-center gap-1.5">
        <span className="truncate font-mono text-[13px] font-bold text-black dark:text-white">
            {orderId ? `#${orderId}` : '—'}
        </span>
        {channel ? <ChannelBadge channel={channel} /> : null}
    </span>
);

/** Número do pedido vinculado no Bling; "—" enquanto não houver vínculo. */
export const BlingOrderCell = ({ blingOrderId }) => {
    const value = CleanIdentityValue(blingOrderId);
    if (!value) {
        return <span className="text-xs text-mono-300 dark:text-mono-700">—</span>;
    }
    return <span className="truncate font-mono text-xs text-mono-600 dark:text-mono-300">{value}</span>;
};

/**
 * Coluna "Pack" (separada do pedido — decisão 2). `sales.pack_id` é coluna
 * gerada do JSON e guarda a STRING 'null' quando não há pack; o backend já
 * normaliza, mas a guarda fica aqui porque "null" na tela é visível.
 */
export const PackCell = ({ packId }) => {
    const value = packId === null || packId === undefined ? '' : String(packId).trim();
    if (!value || value === 'null') {
        return <span className="text-xs text-mono-300 dark:text-mono-700">—</span>;
    }
    return <span className="truncate font-mono text-xs text-sky-700 dark:text-sky-300">{value}</span>;
};

// ── Catálogo de tags: fallback enquanto constants/pedidoTags.js não conhece a key ──

/**
 * Keys que podem chegar do backend antes de entrarem em `PEDIDO_TAG_META`
 * (`{ label, sector, kind }`). Só entram aqui como REDE: se o catálogo
 * conhecer a key, o catálogo manda. Vazio desde o catálogo v3 (2026-09-23);
 * o mecanismo fica.
 */
const LOCAL_TAG_FALLBACK = {};

/** `nf_perda_para_emitir` → "Nf perda para emitir": legível, sem esconder a key. */
function humanizeKey(key) {
    const text = String(key || '')
        .replace(/_/g, ' ')
        .trim();
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

/**
 * Meta de uma tag automática pela key: catálogo → fallback local → nada.
 * Devolve `{ label, sector, kind, action, known }`; `known=false` quando nem
 * o catálogo nem o fallback conhecem (setor e kind ficam null — a linha do
 * wire diz). Urgência NÃO mora aqui (é `urgent` do wire, por linha).
 */
export function ResolveTagMeta(key) {
    const meta = PEDIDO_TAG_META[key] || LOCAL_TAG_FALLBACK[key];
    if (meta) {
        const kind = meta.kind || null;
        return {
            label: PEDIDO_TAG_META[key] ? TagLabel(key) : meta.label,
            sector: meta.sector || null,
            kind,
            action: kind === 'acao',
            known: true,
        };
    }
    return { label: humanizeKey(key), sector: null, kind: null, action: false, known: false };
}

/** Rótulo pt-BR da tag com o mesmo fallback de `ResolveTagMeta`. */
export function LocalTagLabel(key) {
    return ResolveTagMeta(key).label;
}

// ── Chips de filtro ──────────────────────────────────────────────────────────

/**
 * Chip de FILTRO (Urgentes, tags do setor ativo, Para fazer, Com pendência,
 * tags manuais): toggle com contagem. `count === null` = a tag existe na
 * linha mas não conta nem filtra (o backend avalia só por linha) — fica
 * apagado e explica no title. `action` = tag de AÇÃO ("Para fazer"): anel
 * preto sólido — a vez é nossa. `urgent` = chip "Urgentes" com contagem > 0
 * (Regra 1): anel e texto red + quadradinho red — vermelho só para urgência.
 * `swatchClass` pinta um quadradinho com a cor da tag manual.
 * `showCount={false}` = chip de filtro sem contagem (ex.: status do
 * financeiro — o summary não conta por ele).
 * `dueToday` (Giovanni, 2026-10-07) = quantos pedidos da fila VENCEM HOJE
 * (prazo do ML até o corte da Regra 1) — "Mediações para responder 25 · 0
 * hoje": o resto vence depois e responder antes não ganha prazo no ML.
 */
export const FilterChip = ({
    active = false,
    onClick = null,
    count = undefined,
    disabled = false,
    action = false,
    urgent = false,
    swatchClass = null,
    title = undefined,
    showCount = true,
    dueToday = undefined,
    children,
}) => {
    const lineOnly = count === null;
    const base =
        'inline-flex items-center gap-1.5 whitespace-nowrap px-2.5 py-1 text-[11px] font-semibold transition-colors';
    const state = active
        ? 'bg-black text-white dark:bg-white dark:text-black'
        : lineOnly
          ? 'bg-transparent text-mono-400 ring-1 ring-inset ring-dashed ring-mono-300 dark:text-mono-600 dark:ring-mono-700'
          : urgent
            ? 'bg-white text-danger-700 ring-1 ring-inset ring-danger-600 hover:bg-danger-50 dark:bg-mono-950 dark:text-danger-300 dark:ring-danger-400 dark:hover:bg-danger-400/10'
            : action
              ? 'bg-white text-black ring-1 ring-inset ring-black hover:bg-mono-100 dark:bg-mono-950 dark:text-white dark:ring-white dark:hover:bg-mono-900'
              : 'bg-white text-mono-600 ring-1 ring-inset ring-mono-200 hover:bg-mono-100 dark:bg-mono-950 dark:text-mono-300 dark:ring-mono-800 dark:hover:bg-mono-900';
    return (
        <button
            type="button"
            aria-pressed={active}
            disabled={disabled || lineOnly}
            title={lineOnly ? 'Aparece na linha do pedido; sem contagem nem filtro' : title}
            onClick={onClick}
            className={`${base} ${state} disabled:cursor-default disabled:opacity-70`}
        >
            {urgent ? (
                <span
                    className={`h-1.5 w-1.5 shrink-0 ${active ? 'bg-danger-400' : 'bg-danger-600 dark:bg-danger-400'}`}
                />
            ) : swatchClass ? (
                <span className={`h-1.5 w-1.5 shrink-0 ${swatchClass}`} />
            ) : null}
            {children}
            {!showCount ? null : count === undefined ? (
                <span className="tabular-nums opacity-50">…</span>
            ) : lineOnly ? null : (
                <span className="tabular-nums opacity-60">{FormatInt(count)}</span>
            )}
            {typeof dueToday === 'number' && !lineOnly && Number(count) > 0 ? (
                <span
                    className={`tabular-nums ${
                        active
                            ? 'opacity-80'
                            : dueToday > 0
                              ? 'text-danger-700 dark:text-danger-300'
                              : 'font-normal text-mono-400 dark:text-mono-500'
                    }`}
                >
                    · {FormatInt(dueToday)} hoje
                </span>
            ) : null}
        </button>
    );
};

// ── Menu de blocos (Todos | setores VISÍVEIS) ────────────────────────────────

const GRID_BY_COUNT = {
    1: 'sm:grid-cols-1',
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-3',
    4: 'sm:grid-cols-4',
};

/**
 * Mesmo padrão do `menuTabs` de `pages/Rmas.jsx`: grid `gap-px`, bloco ativo
 * com borda superior preta 3px, ícone + rótulo CAPS + números do bloco
 * (`SectorBlockNumbers`, rodada 5: PENDÊNCIAS — "para fazer" — em grande,
 * preto/branco; "N urgentes" em vermelho ao lado quando > 0; embaixo, "T no
 * total"). Diferente do `SectorBlocks`
 * compartilhado, recebe `sectors` = setores que o usuário pode ver
 * (`summary.visible_sectors`, decisão 8) e só renderiza esses. `active` = ''
 * (Todos) ou setor.
 */
export const SectorMenu = ({ summary = null, sectors = SECTOR_ORDER, active = '', onChange }) => {
    const visible = SECTOR_ORDER.filter((sector) => sectors.includes(sector));
    const blocks = [
        { key: '', label: 'Todos', Icon: ClipboardText, sector: null },
        ...visible.map((sector) => ({
            key: sector,
            label: SECTOR_META[sector].label,
            Icon: SECTOR_META[sector].Icon,
            sector,
        })),
    ];
    const cols = GRID_BY_COUNT[blocks.length] || GRID_BY_COUNT[4];
    return (
        <div
            className={`grid grid-cols-2 gap-px border border-mono-200 bg-mono-200 dark:border-mono-800 dark:bg-mono-800 ${cols}`}
        >
            {blocks.map((block) => {
                const isActive = (active || '') === block.key;
                const BlockIcon = block.Icon;
                return (
                    <button
                        key={block.key || 'todos'}
                        type="button"
                        onClick={() => onChange?.(block.key)}
                        aria-pressed={isActive}
                        className={`flex flex-col gap-1 border-t-[3px] px-4 py-2.5 text-left transition-colors ${
                            isActive
                                ? 'border-black bg-white dark:border-white dark:bg-mono-950'
                                : 'border-transparent bg-white hover:bg-mono-50 dark:bg-mono-950 dark:hover:bg-mono-900'
                        }`}
                    >
                        <span
                            className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest ${
                                isActive ? 'text-black dark:text-white' : 'text-mono-400 dark:text-mono-600'
                            }`}
                        >
                            {BlockIcon ? <BlockIcon size={13} /> : null} {block.label}
                        </span>
                        <SectorBlockNumbers summary={summary} sector={block.sector} />
                    </button>
                );
            })}
        </div>
    );
};

/** Rodapé de paginação no servidor: "1–50 de N" + Anterior/Próxima. */
export const PagerBar = ({ page, pageSize, total, onPage, busy = false }) => {
    const safeTotal = Math.max(0, Number(total) || 0);
    const pages = Math.max(1, Math.ceil(safeTotal / pageSize));
    const current = Math.min(Math.max(1, page), pages);
    const start = safeTotal === 0 ? 0 : (current - 1) * pageSize + 1;
    const end = Math.min(current * pageSize, safeTotal);
    return (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-mono-500 dark:text-mono-400">
            <span className="tabular-nums">
                {FormatInt(start)}–{FormatInt(end)} de {FormatInt(safeTotal)}
            </span>
            <span className="flex items-center gap-2">
                <span className="text-xs tabular-nums text-mono-400 dark:text-mono-500">
                    página {FormatInt(current)} de {FormatInt(pages)}
                </span>
                <button
                    type="button"
                    className={SECONDARY_BUTTON_CLASSES}
                    disabled={busy || current <= 1}
                    onClick={() => onPage(current - 1)}
                >
                    Anterior
                </button>
                <button
                    type="button"
                    className={SECONDARY_BUTTON_CLASSES}
                    disabled={busy || current >= pages}
                    onClick={() => onPage(current + 1)}
                >
                    Próxima
                </button>
            </span>
        </div>
    );
};

/**
 * Estação de inspeção em TELA CHEIA — mesmo takeover de pages/Rmas.jsx
 * (stationOpen + inspectingRef + Esc guardado): com o assistente aberto o Esc
 * NÃO fecha, para não perder uma inspeção em curso. Quem embute decide o que
 * "abrir caso" faz e o que recarregar quando uma inspeção é registrada.
 */
export const InspectionTakeover = ({ open, onClose, toast, onOpenCase, onChanged, canLogistics, canInspect }) => {
    const inspectingRef = useRef(false);
    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const onKey = (e) => {
            if (e.key === 'Escape' && !inspectingRef.current) {
                onClose();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) {
        return null;
    }
    return (
        <div className="fixed inset-0 z-40 flex flex-col bg-mono-50 dark:bg-black">
            <header className="flex items-center justify-between border-b border-mono-200 bg-white px-6 py-3 dark:border-mono-800 dark:bg-mono-950">
                <h2 className="text-sm font-bold uppercase tracking-widest text-mono-500 dark:text-mono-400">
                    ▮ Estação de inspeção
                </h2>
                <button
                    type="button"
                    onClick={onClose}
                    className="border border-mono-300 px-4 py-1.5 text-sm font-semibold text-mono-600 transition-colors hover:border-black hover:text-black dark:border-mono-700 dark:text-mono-300 dark:hover:border-white dark:hover:text-white"
                >
                    Sair (Esc)
                </button>
            </header>
            <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col justify-start overflow-y-auto p-6 pt-10">
                <InspectionStation
                    fullscreen
                    onInspectingChange={(v) => {
                        inspectingRef.current = v;
                    }}
                    toast={toast}
                    onOpenCase={onOpenCase}
                    onChanged={onChanged}
                    canLogistics={canLogistics}
                    canInspect={canInspect}
                />
            </main>
        </div>
    );
};

// ── Sincronizar tudo (dono, 2026-09-23) ──────────────────────────────────────
// `POST /pedidos/sync-all` (orders:write) enfileira, SEM esperar, os 6 jobs de
// sync com o ML (pedidos novos, status, reclamações, mensagens, envios,
// devoluções) — um por vez, no servidor — e responde na hora. O status
// (`GET /pedidos/sync-all/status`, orders:read) é da RODADA no servidor:
// `running`, `done`/`total` (o "n/6"), `jobs[].round_state` (queued · running
// · waiting · done · failed · cancelled), `last_synced_at` (o `last_run` mais
// ANTIGO entre os jobs) e `failed_sources` (rótulos das fontes cuja última
// execução FALHOU — o last_run delas é a hora da falha). Como o progresso vem
// do servidor, F5 ou outra pessoa clicando mostram o mesmo "n/6".

const NO_SYNC_JOBS = [];
// Denominador do "n/6" enquanto o status ainda não chegou.
const SYNC_ALL_JOB_COUNT = Object.keys(SYNC_ALL_JOB_LABELS).length;
// Falhas SEGUIDAS do status antes de desistir de acompanhar.
const SYNC_MAX_POLL_FAILURES = 3;

function syncJobsOf(status) {
    return Array.isArray(status?.jobs) ? status.jobs : NO_SYNC_JOBS;
}

/** Jobs da rodada num estado (`round_state`), pelo rótulo pt-BR. */
function syncRoundLabels(status, roundState) {
    return syncJobsOf(status)
        .filter((job) => job.round_state === roundState)
        .map((job) => SyncAllJobLabel(job));
}

/**
 * Fontes que FALHARAM na rodada: `round_state` failed, ou `waiting` (pulada
 * por já estar rodando pelo agendador — ou com a execução anterior ainda
 * drenando) cuja execução terminou FAILED: não sincronizou nesta rodada.
 */
function syncRoundFailedLabels(status) {
    return syncJobsOf(status)
        .filter(
            (job) => job.round_state === 'failed' || (job.round_state === 'waiting' && job.status === SYNC_JOB_FAILED),
        )
        .map((job) => SyncAllJobLabel(job));
}

/** Title de "Última sincronização": a hora de cada fonte, uma por linha. */
function syncSourcesTitle(status) {
    const jobs = syncJobsOf(status);
    if (!jobs.length) {
        return 'Hora da fonte menos atualizada entre as sincronizações com o Mercado Livre';
    }
    const lines = jobs.map((job) => {
        const at = FormatShortInstant(job.last_run) || 'nunca';
        const state = status?.running
            ? SYNC_ROUND_STATE_LABELS[job.round_state]
            : job.status === SYNC_JOB_RUNNING
              ? 'rodando agora'
              : job.status === SYNC_JOB_FAILED
                ? 'falhou'
                : job.available === false
                  ? 'indisponível neste servidor'
                  : '';
        return `${SyncAllJobLabel(job)}: ${at}${state ? ` (${state})` : ''}`;
    });
    return ['Última execução de cada fonte (a mais antiga é a mostrada):', ...lines].join('\n');
}

/** Segundos até poder sincronizar de novo, do 409: `details.retry_after` (ou `details.cooldown_until`). */
function syncRetryAfterSeconds(err) {
    const direct = Number(err?.details?.retry_after);
    if (Number.isFinite(direct) && direct > 0) {
        return Math.ceil(direct);
    }
    const until = err?.details?.cooldown_until ? new Date(err.details.cooldown_until).getTime() : Number.NaN;
    if (Number.isFinite(until) && until > Date.now()) {
        return Math.ceil((until - Date.now()) / 1000);
    }
    return null;
}

/**
 * Estado do "Sincronizar tudo" da lista. Carrega o status ao montar (texto
 * "Última sincronização"; rodada em curso — F5, outra aba/pessoa — já entra
 * acompanhando); `start()` dispara e acompanha a cada 5 s enquanto `running`.
 * Ao terminar chama `onFinished` (recarregar summary + lista) e avisa:
 * "Sincronização concluída" ou quais fontes falharam (`round_state` failed)
 * ou não rodaram (cancelled). 409 (cooldown de 2 min) → "Aguarde X s…".
 */
export function useSyncAll({ toast, onFinished }) {
    const [status, setStatus] = useState(null);
    const [tracking, setTracking] = useState(false);
    const [starting, setStarting] = useState(false);
    const timerRef = useRef(null);
    const aliveRef = useRef(true);
    const failuresRef = useRef(0);
    // busy = disparo em voo OU acompanhando; tracking = acompanhando a rodada.
    const busyRef = useRef(false);
    const trackingRef = useRef(false);
    const toastRef = useRef(toast);
    toastRef.current = toast;
    const finishedRef = useRef(onFinished);
    finishedRef.current = onFinished;
    // O timer chama sempre o `poll` mais recente (atribuído logo abaixo dele).
    const pollRef = useRef(null);

    const stopTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    // Um timer só: reagendar sempre descarta o anterior (nunca duas cadeias).
    const schedulePoll = useCallback(
        (delay = SYNC_ALL_POLL_MS) => {
            stopTimer();
            timerRef.current = setTimeout(() => pollRef.current?.(), delay);
        },
        [stopTimer],
    );

    const beginTracking = useCallback(
        (delay) => {
            busyRef.current = true;
            trackingRef.current = true;
            failuresRef.current = 0;
            setTracking(true);
            schedulePoll(delay);
        },
        [schedulePoll],
    );

    const stopTracking = useCallback(() => {
        busyRef.current = false;
        trackingRef.current = false;
        setTracking(false);
    }, []);

    const finish = useCallback(
        (data) => {
            stopTracking();
            const failed = syncRoundFailedLabels(data);
            const cancelled = syncRoundLabels(data, 'cancelled');
            const parts = [];
            if (failed.length) {
                parts.push(`falhou em: ${failed.join(', ')}`);
            }
            if (cancelled.length) {
                parts.push(`não rodou (servidor reiniciando): ${cancelled.join(', ')}`);
            }
            if (parts.length) {
                toastRef.current?.info(`Sincronização concluída — ${parts.join('; ')}.`);
            } else {
                toastRef.current?.success('Sincronização concluída');
            }
            finishedRef.current?.();
        },
        [stopTracking],
    );

    const poll = useCallback(async () => {
        timerRef.current = null;
        let data = null;
        try {
            data = await api.syncAllStatus();
        } catch {
            if (!aliveRef.current) {
                return;
            }
            failuresRef.current += 1;
            if (failuresRef.current >= SYNC_MAX_POLL_FAILURES) {
                stopTracking();
                toastRef.current?.error(
                    'Não foi possível acompanhar a sincronização — ela segue no servidor; atualize a lista em alguns minutos.',
                );
                return;
            }
            schedulePoll();
            return;
        }
        // Efeito desmontado/refeito no meio da leitura: a remontagem retoma.
        if (!aliveRef.current) {
            return;
        }
        failuresRef.current = 0;
        setStatus(data || null);
        if (data?.running) {
            schedulePoll();
            return;
        }
        finish(data);
    }, [finish, schedulePoll, stopTracking]);
    pollRef.current = poll;

    // Montagem (e efeito refeito — StrictMode/Fast Refresh — com o timer
    // cancelado pela limpeza): status para o texto; rodada em curso →
    // acompanha (ou RETOMA); rodada que acabou enquanto o efeito estava
    // desligado → fecha o acompanhamento com o aviso.
    useEffect(() => {
        aliveRef.current = true;
        api.syncAllStatus()
            .then((data) => {
                if (!aliveRef.current) {
                    return;
                }
                setStatus(data || null);
                if (data?.running) {
                    beginTracking(SYNC_ALL_POLL_MS);
                } else if (trackingRef.current) {
                    finish(data);
                }
            })
            .catch(() => {
                // Sem status o texto fica "—"; o botão continua funcionando.
            });
        return () => {
            aliveRef.current = false;
            stopTimer();
        };
    }, [beginTracking, finish, stopTimer]);

    const start = useCallback(async () => {
        if (busyRef.current) {
            return;
        }
        busyRef.current = true;
        setStarting(true);
        try {
            const result = await api.syncAll();
            if (!aliveRef.current) {
                return;
            }
            const queued = [
                ...(Array.isArray(result?.started) ? result.started : []),
                ...(Array.isArray(result?.skipped_running) ? result.skipped_running : []),
            ];
            if (!queued.length) {
                busyRef.current = false;
                toastRef.current?.info('Nenhuma fonte disponível para sincronizar neste servidor.');
                return;
            }
            // Primeira leitura já: o "0/6" aparece com a rodada do servidor.
            beginTracking(0);
        } catch (err) {
            busyRef.current = false;
            if (!aliveRef.current) {
                return;
            }
            if (err?.status === 409) {
                const seconds = syncRetryAfterSeconds(err);
                toastRef.current?.info(
                    seconds
                        ? `Aguarde ${FormatInt(seconds)} s para sincronizar de novo.`
                        : err?.message || 'Aguarde para sincronizar de novo.',
                );
            } else {
                toastRef.current?.error(err?.message || 'Não foi possível iniciar a sincronização.');
            }
        } finally {
            if (aliveRef.current) {
                setStarting(false);
            }
        }
    }, [beginTracking]);

    const total = Number(status?.total) > 0 ? Number(status.total) : SYNC_ALL_JOB_COUNT;
    const done = Math.min(Math.max(0, Number(status?.done) || 0), total);
    return {
        status,
        syncing: tracking || starting,
        tracking,
        progress: { done: tracking && status?.running ? done : 0, total },
        lastSyncedAt: status?.last_synced_at || null,
        failedSources: Array.isArray(status?.failed_sources) ? status.failed_sources : NO_SYNC_JOBS,
        start,
    };
}

/**
 * Cabeçalho da lista: "Última sincronização: dd/mm hh:mm" (a fonte MENOS
 * atualizada — é o quão atualizado está o todo; title com a hora de cada
 * fonte) + "· falhou: X" em cor de aviso quando a última execução de alguma
 * fonte falhou (a hora dela é a da falha, não de uma sincronização) + botão
 * "Sincronizar tudo" ("Sincronizando… 2/6" com o ícone girando enquanto a
 * rodada corre). Sem `orders:write` o botão fica desabilitado, com o motivo
 * no title.
 */
export const SyncAllControl = ({ sync, canSync = false }) => {
    const { status, syncing, tracking, progress, lastSyncedAt, failedSources = NO_SYNC_JOBS, start } = sync;
    const lastText = FormatShortInstant(lastSyncedAt) || '—';
    const label = tracking
        ? `Sincronizando… ${FormatInt(progress.done)}/${FormatInt(progress.total)}`
        : syncing
          ? 'Sincronizando…'
          : 'Sincronizar tudo';
    const title = !canSync
        ? 'Sem permissão para sincronizar (exige orders:write)'
        : syncing
          ? 'Sincronizando com o Mercado Livre, uma fonte por vez: pedidos novos, status, reclamações, mensagens, envios e devoluções'
          : 'Buscar agora no Mercado Livre: pedidos novos, mudanças de status, reclamações/mediações, mensagens, envios e devoluções (NF-e de devolução)';
    return (
        <span className="flex flex-wrap items-center gap-2">
            <span
                className="whitespace-nowrap text-[11px] tabular-nums text-mono-400 dark:text-mono-500"
                title={syncSourcesTitle(status)}
            >
                Última sincronização: {lastText}
            </span>
            {failedSources.length > 0 && (
                <span className="text-[11px] text-warning-text dark:text-warning-300" title={syncSourcesTitle(status)}>
                    · falhou: {failedSources.join(', ')}
                </span>
            )}
            <button
                type="button"
                onClick={start}
                disabled={!canSync || syncing}
                aria-busy={syncing}
                title={title}
                className={SECONDARY_BUTTON_CLASSES}
            >
                <ArrowClockwise size={15} className={syncing ? 'animate-spin' : ''} />
                <span className="tabular-nums">{label}</span>
            </button>
        </span>
    );
};
